import type { Node } from "@mapcraft-tasks/client-store";
import {
	useAllTags,
	useDispatch,
	useReplica,
	useSearch,
	useStore,
	useTagColors,
	useToday
} from "@mapcraft-tasks/client-store/react";
import {
	NODE_STATUSES,
	type NodeStatus,
	normalizeTags,
	type Recurrence,
	TAG_COLORS,
	type TagColor
} from "@mapcraft-tasks/domain";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { syncStatusBarColor, usePhone } from "../lib/mobile";
import type { useOutlineEngine } from "../outline/controller";
import { Drawer } from "./mobile/drawer";
import { TagChip } from "./tag-chip";

/**
 * Оверлеи — быстрые меню поверх аутлайна. Все открываются аккордами и закрываются `Esc`;
 * мышь всюду тоже работает, но интерфейс рассчитан на то, что за ней не тянутся.
 *
 * Модалок «подождите, сохраняем» здесь нет и быть не может: `dispatch` синхронен,
 * ждать нечего (ui-spec Область 11).
 */
export function Overlays({ engine }: { engine: ReturnType<typeof useOutlineEngine> }) {
	const { state, actions } = engine;
	const overlay = state.overlay;
	if (overlay === null) {
		return null;
	}

	switch (overlay.kind) {
		case "status":
			return (
				<Sheet title="Статус" onClose={() => actions.setOverlay(null)}>
					<StatusMenu onPick={actions.setStatus} />
				</Sheet>
			);
		case "role":
			return (
				<Sheet title="Роль" onClose={() => actions.setOverlay(null)}>
					<Choices
						items={[
							{ key: "task", label: "задача", hint: "rt" },
							{ key: "project", label: "проект", hint: "rp" },
							{ key: "plain", label: "просто текст", hint: "rn" },
							{ key: "focus", label: "фокус", hint: "rf" }
						]}
						onPick={(key) => actions.setRole(key as "task" | "project" | "plain" | "focus")}
					/>
				</Sheet>
			);
		case "date":
			return (
				<Sheet title="Срок" onClose={() => actions.setOverlay(null)}>
					<DatePicker onPick={actions.schedule} />
				</Sheet>
			);
		case "recurrence":
			return (
				<Sheet title="Повторение" onClose={() => actions.setOverlay(null)}>
					<RecurrenceForm engine={engine} />
				</Sheet>
			);
		case "tags":
			return (
				<Sheet title="Теги" onClose={() => actions.setOverlay(null)}>
					<TagsForm engine={engine} />
				</Sheet>
			);
		case "search":
			return (
				<Sheet title="Поиск" onClose={() => actions.setOverlay(null)}>
					<SearchBox
						onPick={(node) => {
							actions.setOverlay(null);
							actions.zoom(node.id);
						}}
					/>
				</Sheet>
			);
		case "picker":
			return (
				<Sheet
					title={overlay.purpose === "move" ? "Переместить в…" : "Вплести в…"}
					onClose={() => actions.setOverlay(null)}
				>
					<SearchBox
						onPick={(node) => {
							const ids = actions.selectionIds();
							actions.run(
								overlay.purpose === "move"
									? [{ name: "moveNodes", input: { nodeIds: ids, parentId: node.id } }]
									: ids.map((id) => ({
											name: "addPlacement",
											input: { nodeId: id, containerId: node.id }
										}))
							);
							actions.setOverlay(null);
						}}
					/>
				</Sheet>
			);
		case "palette":
			return (
				<Sheet title="Команды" onClose={() => actions.setOverlay(null)}>
					<CommandPalette engine={engine} />
				</Sheet>
			);
		case "confirm":
			return (
				<Sheet title="Подтверждение" onClose={() => actions.setOverlay(null)}>
					<Confirm
						message={overlay.message}
						label={overlay.confirmLabel}
						danger={overlay.danger === true}
						onConfirm={() => {
							overlay.onConfirm();
							actions.setOverlay(null);
						}}
						onCancel={() => actions.setOverlay(null)}
					/>
				</Sheet>
			);
	}
}

/**
 * Подтверждение — единственный оверлей с готовым ответом по умолчанию: `Enter` соглашается,
 * `Esc` отменяет. Del, Enter — и строки нет, руку с клавиатуры убирать не надо.
 *
 * Клавиша ловится на документе в фазе перехвата, а не фокусом кнопки: аутлайн слушает
 * тот же `keydown` на документе, и без перехвата его `Enter` увёл бы фокус в редактор
 * строки под оверлеем — ровно то, из-за чего кнопка «теряла» нажатие.
 */
function Confirm({
	message,
	label,
	danger,
	onConfirm,
	onCancel
}: {
	message: string;
	label: string;
	danger: boolean;
	onConfirm: () => void;
	onCancel: () => void;
}) {
	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Enter") {
				e.preventDefault();
				e.stopPropagation();
				onConfirm();
			}
		};
		document.addEventListener("keydown", handler, true);
		return () => document.removeEventListener("keydown", handler, true);
	}, [onConfirm]);

	return (
		<>
			<p className="px-4 py-3">{message}</p>
			<div className="flex items-center justify-end gap-2 border-t border-stroke-soft px-4 py-3">
				<button type="button" className="btn" onClick={onCancel}>
					Отмена <span className="kbd">Esc</span>
				</button>
				<button
					type="button"
					className={`btn ${danger ? "btn-danger" : "btn-primary"}`}
					onClick={onConfirm}
					autoFocus
				>
					{label} <span className="kbd">Enter</span>
				</button>
			</div>
		</>
	);
}

function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
	const phone = usePhone();

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.preventDefault();
				e.stopPropagation();
				onClose();
			}
		};
		document.addEventListener("keydown", handler, true);
		return () => document.removeEventListener("keydown", handler, true);
	}, [onClose]);

	/*
	 * На телефоне меню приходит снизу, от пальца, и утаскивается обратно вниз. Всплывающее
	 * окно посреди экрана там означало бы, что до его кнопок надо дотягиваться второй рукой,
	 * а закрыть его можно только прицельным промахом мимо.
	 */
	if (phone) {
		return (
			<Drawer open side="bottom" title={title} onClose={onClose}>
				{children}
			</Drawer>
		);
	}

	return (
		<div className="fixed inset-0 z-40 flex items-start justify-center pt-[18vh]" onMouseDown={onClose}>
			{/* Затемнение слабое: под ним живой аутлайн, и он должен читаться. */}
			<div className="absolute inset-0 bg-app/60" />
			<div
				className="popover relative w-[min(560px,92vw)] overflow-hidden"
				onMouseDown={(e) => e.stopPropagation()}
			>
				<div className="section-label border-b border-stroke-soft px-4 py-2">{title}</div>
				{children}
			</div>
		</div>
	);
}

const STATUS_LABELS: Record<NodeStatus, string> = {
	open: "открыта",
	paused: "на паузе",
	someday: "когда-нибудь",
	done: "сделано",
	cancelled: "не буду делать"
};

function StatusMenu({ onPick }: { onPick: (status: NodeStatus | null) => void }) {
	return (
		<Choices
			items={[
				...NODE_STATUSES.map((s) => ({ key: s, label: STATUS_LABELS[s], hint: "" })),
				{ key: "", label: "снять статус", hint: "" }
			]}
			onPick={(key) => onPick(key === "" ? null : (key as NodeStatus))}
		/>
	);
}

function Choices({
	items,
	onPick
}: {
	items: { key: string; label: string; hint?: string }[];
	onPick: (key: string) => void;
}) {
	const [index, setIndex] = useState(0);
	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "ArrowDown") {
				e.preventDefault();
				setIndex((i) => Math.min(items.length - 1, i + 1));
			} else if (e.key === "ArrowUp") {
				e.preventDefault();
				setIndex((i) => Math.max(0, i - 1));
			} else if (e.key === "Enter") {
				e.preventDefault();
				const item = items[index];
				if (item) {
					onPick(item.key);
				}
			}
		};
		document.addEventListener("keydown", handler, true);
		return () => document.removeEventListener("keydown", handler, true);
	}, [items, index, onPick]);

	return (
		/* Пункт меню — рецепт контекстного меню Tana: 12px весом 375, радиус 3.75px,
		   подсветка та же `--bg-active`, что у активного таба и hover кнопки. */
		<ul className="p-[5px]">
			{items.map((item, i) => (
				<li key={item.key}>
					<button
						type="button"
						className={`menu-item flex w-full items-baseline justify-between gap-[9px] rounded-sm px-3 py-[5px] text-left text-xs font-light ${
							i === index ? "bg-active text-ink-strong" : ""
						}`}
						onMouseEnter={() => setIndex(i)}
						onClick={() => onPick(item.key)}
					>
						<span>{item.label}</span>
						{item.hint ? <span className="kbd">{item.hint}</span> : null}
					</button>
				</li>
			))}
		</ul>
	);
}

/**
 * Держит подсвеченный пункт в видимой части скроллящегося списка: стрелками легко уехать
 * за нижний край попапа и потерять фокус из виду. `block: "nearest"` не дёргает список,
 * пока пункт и так виден.
 */
function useScrollToActive(index: number) {
	const listRef = useRef<HTMLUListElement>(null);
	useEffect(() => {
		const item = listRef.current?.children[index];
		item?.scrollIntoView({ block: "nearest" });
	}, [index]);
	return listRef;
}

function DatePicker({ onPick }: { onPick: (date: string | null) => void }) {
	const today = useToday();
	const [value, setValue] = useState(today);
	return (
		<div className="px-4 py-4">
			<div className="mb-3 flex flex-wrap items-center gap-2">
				<button type="button" className="btn" onClick={() => onPick(today)}>
					сегодня <span className="kbd">td</span>
				</button>
				<button type="button" className="btn" onClick={() => onPick(shift(today, 1))}>
					завтра <span className="kbd">tm</span>
				</button>
				<button type="button" className="btn" onClick={() => onPick(shift(today, 7))}>
					+7 дней
				</button>
				<button
					type="button"
					className="ml-auto flex items-center gap-2 rounded-sm px-2 py-1 text-xs font-light text-ink-muted transition-colors hover:bg-active hover:text-ink-strong"
					onClick={() => onPick(null)}
				>
					снять <span className="kbd">cd</span>
				</button>
			</div>
			<div className="flex gap-2">
				<input
					type="date"
					value={value}
					onChange={(e) => setValue(e.target.value)}
					className="field tabular flex-1"
				/>
				<button type="button" className="btn btn-primary" onClick={() => onPick(value)}>
					Поставить
				</button>
			</div>
		</div>
	);
}

function RecurrenceForm({ engine }: { engine: ReturnType<typeof useOutlineEngine> }) {
	const { actions } = engine;
	const [period, setPeriod] = useState<Recurrence["period"]>("day");
	const [count, setCount] = useState(1);

	const apply = () => {
		const recurrence: Recurrence = { period, count };
		for (const id of actions.selectionIds()) {
			actions.run([{ name: "setRecurrence", input: { nodeId: id, recurrence } }]);
		}
		actions.setOverlay(null);
	};

	return (
		<div className="space-y-3 px-4 py-4">
			<div className="flex items-center gap-2">
				<span className="text-sm text-ink-muted">каждый</span>
				{(["day", "week", "month"] as const).map((p) => (
					<button
						key={p}
						type="button"
						className={`btn ${period === p ? "border-link bg-link-wash text-link" : ""}`}
						onClick={() => setPeriod(p)}
					>
						{p === "day" ? "день" : p === "week" ? "неделю" : "месяц"}
					</button>
				))}
			</div>
			<label className="flex items-center gap-2">
				<span className="text-sm text-ink-muted">цель за период</span>
				<input
					type="number"
					min={1}
					value={count}
					onChange={(e) => setCount(Math.max(1, Number(e.target.value)))}
					className="field tabular w-16"
				/>
				<span className="text-sm text-ink-faint">раз</span>
			</label>
			<div className="flex items-center justify-between border-t border-stroke-soft pt-3">
				<button
					type="button"
					className="rounded-sm px-2 py-1 text-xs font-light text-ink-muted transition-colors hover:bg-active hover:text-ink-strong"
					onClick={() => {
						for (const id of actions.selectionIds()) {
							actions.run([{ name: "setRecurrence", input: { nodeId: id, recurrence: null } }]);
						}
						actions.setOverlay(null);
					}}
				>
					снять повторение
				</button>
				<button type="button" className="btn btn-primary" onClick={apply}>
					Сохранить
				</button>
			</div>
		</div>
	);
}

/**
 * Редактор тегов: стоящие — плашками с крестиком, под полем — подсказки из уже
 * существующих тегов и «создать», если набранного ещё нет.
 *
 * Правка применяется СРАЗУ, на каждое добавление и снятие, как и в остальных оверлеях:
 * `dispatch` синхронен, и кнопка «сохранить» означала бы лишь ещё одно нажатие и способ
 * потерять набранное по `Esc`. Каждая правка кладёт свою запись в undo.
 *
 * При выделении нескольких нод плашка — объединение их тегов. Тег, что стоит не у всех,
 * нарисован пунктиром; выбрать его в подсказках — значит проставить остальным.
 *
 * Регистр в домене значим, но создать `Work` рядом с уже живущим `work` здесь нельзя:
 * подсказка подставит существующее написание. Почти-дубль почти всегда опечатка, а не
 * намерение, — а развести их потом руками куда дороже.
 *
 * Цвет — по щелчку на плашке: под строкой открывается палитра этого тега. Цвет — свойство
 * тега, а не выделенных нод, поэтому меняется он сразу везде. Только что созданному тегу
 * палитра открывается сама: цвет выбирают как раз тогда, когда тег заводят.
 */
function TagsForm({ engine }: { engine: ReturnType<typeof useOutlineEngine> }) {
	const { actions } = engine;
	const replica = useReplica();
	const allTags = useAllTags();
	const colors = useTagColors();
	const [coloring, setColoring] = useState<string | null>(null);
	// Выделение снимается один раз: под открытым оверлеем оно не меняется, а пересчёт
	// на каждый рендер тянул бы за собой зависимости всех мемо ниже.
	const [ids] = useState(() => actions.selectionIds());
	const nodes = useMemo(
		() => ids.map((id) => replica.nodes.get(id)).filter((n): n is Node => n !== undefined),
		[ids, replica]
	);

	const present = useMemo(() => {
		const counts = new Map<string, number>();
		for (const node of nodes) {
			for (const tag of node.tags) {
				counts.set(tag, (counts.get(tag) ?? 0) + 1);
			}
		}
		return counts;
	}, [nodes]);
	const onAll = (tag: string) => present.get(tag) === nodes.length;

	const [query, setQuery] = useState("");
	const [index, setIndex] = useState(0);
	const inputRef = useRef<HTMLInputElement>(null);
	const listRef = useScrollToActive(index);

	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	const typed = normalizeTags([query])[0] ?? "";
	const options = useMemo(() => {
		const q = typed.toLowerCase();
		const matches = allTags
			.filter(({ tag }) => present.get(tag) !== nodes.length && tag.toLowerCase().includes(q))
			.map(({ tag, count }) => ({ kind: "existing" as const, tag, count }));
		const known = typed !== "" && allTags.some(({ tag }) => tag.toLowerCase() === q);
		if (typed === "" || known) {
			return matches;
		}
		const create = { kind: "create" as const, tag: typed, count: 0 };
		// Ничего похожего нет — создание единственный смысл набранного, ставим его первым,
		// чтобы хватило Enter. Иначе — последним: Enter берёт подсказку, а не опечатку.
		return matches.length === 0 ? [create] : [...matches, create];
	}, [allTags, typed, present, nodes.length]);

	useEffect(() => setIndex(0), [query]);

	const retag = (label: string, next: (tags: string[]) => string[]) => {
		const changed = nodes
			.map((node) => ({ node, tags: next(node.tags) }))
			.filter(({ node, tags }) => tags.join("\u0000") !== node.tags.join("\u0000"));
		if (changed.length === 0) {
			return;
		}
		actions.run(
			changed.map(({ node, tags }) => ({ name: "setTags", input: { nodeId: node.id, tags } })),
			{
				label,
				commands: changed.map(({ node }) => ({ name: "setTags", input: { nodeId: node.id, tags: node.tags } }))
			}
		);
	};

	const add = (tag: string) => {
		// Существующее написание главнее набранного — см. шапку про почти-дубли.
		const existing = allTags.find((t) => t.tag.toLowerCase() === tag.toLowerCase())?.tag;
		const canonical = existing ?? tag;
		retag("тег", (tags) => (tags.includes(canonical) ? tags : [...tags, canonical]));
		setColoring(existing === undefined ? canonical : null);
		setQuery("");
		inputRef.current?.focus();
	};

	const remove = (tag: string) => {
		retag("снять тег", (tags) => tags.filter((t) => t !== tag));
		if (coloring === tag) {
			setColoring(null);
		}
		inputRef.current?.focus();
	};

	const paint = (tag: string, color: TagColor | null) => {
		const before = colors.get(tag) ?? null;
		if (before !== color) {
			actions.run([{ name: "setTagColor", input: { tag, color } }], {
				label: "цвет тега",
				commands: [{ name: "setTagColor", input: { tag, color: before } }]
			});
		}
		setColoring(null);
		inputRef.current?.focus();
	};

	const chips = [...present.keys()];

	return (
		<div>
			{/* Щелчок по пустому месту строки — в поле: плашки и поле читаются как одно целое. */}
			<div
				className="flex min-h-10 flex-wrap items-center gap-[5px] border-b border-stroke-soft px-4 py-2"
				onMouseDown={(e) => {
					if (e.target === e.currentTarget) {
						e.preventDefault();
						inputRef.current?.focus();
					}
				}}
			>
				{chips.map((tag) => (
					<TagChip
						key={tag}
						tag={tag}
						color={colors.get(tag)}
						className={`tag-chip-pick ${onAll(tag) ? "" : "tag-chip-partial"}`}
						title={onAll(tag) ? "цвет" : `есть у ${present.get(tag)} из ${nodes.length}`}
						active={coloring === tag}
						onPick={() => setColoring((current) => (current === tag ? null : tag))}
					>
						<button
							type="button"
							className="tag-chip-x"
							aria-label={`снять тег ${tag}`}
							tabIndex={-1}
							onMouseDown={(e) => e.preventDefault()}
							onClick={(e) => {
								// Не всплывать до плашки: её щелчок открывает палитру снимаемого тега.
								e.stopPropagation();
								remove(tag);
							}}
						>
							×
						</button>
					</TagChip>
				))}
				<input
					ref={inputRef}
					value={query}
					onChange={(e) => setQuery(e.target.value.replace(",", ""))}
					placeholder={chips.length > 0 ? "ещё тег" : "добавить тег"}
					aria-label="тег"
					className="min-w-[10ch] flex-1 bg-transparent py-px outline-none placeholder:text-ink-faint"
					onKeyDown={(e) => {
						if (e.key === "ArrowDown") {
							e.preventDefault();
							setIndex((i) => Math.min(options.length - 1, i + 1));
						} else if (e.key === "ArrowUp") {
							e.preventDefault();
							setIndex((i) => Math.max(0, i - 1));
						} else if (e.key === "Enter") {
							e.preventDefault();
							const option = options[index];
							if (option) {
								add(option.tag);
							} else if (typed === "") {
								actions.setOverlay(null);
							}
						} else if (e.key === "," && typed !== "") {
							// Запятая — «добавить ровно набранное», в обход подсветки: так
							// набирают несколько тегов подряд, не глядя в список.
							e.preventDefault();
							add(typed);
						} else if (e.key === "Backspace" && query === "" && chips.length > 0) {
							e.preventDefault();
							remove(chips[chips.length - 1]!);
						}
					}}
				/>
			</div>

			{coloring !== null && (
				<TagPalette tag={coloring} color={colors.get(coloring)} onPick={(color) => paint(coloring, color)} />
			)}

			<ul ref={listRef} className="max-h-64 overflow-y-auto p-[5px]">
				{options.map((option, i) => (
					<li key={`${option.kind}:${option.tag}`}>
						<button
							type="button"
							className={`menu-item flex w-full items-baseline justify-between gap-[9px] rounded-sm px-3 py-[5px] text-left text-xs font-light ${
								i === index ? "bg-active text-ink-strong" : ""
							}`}
							/* См. `SearchBox`: `mousemove`, чтобы автоскролл не перехватывал выбор. */
							onMouseMove={() => setIndex(i)}
							onMouseDown={(e) => e.preventDefault()}
							onClick={() => add(option.tag)}
						>
							{option.kind === "create" ? (
								<span className="flex items-baseline gap-[7px]">
									<span className="text-ink-muted">создать</span>
									<TagChip tag={option.tag} color={colors.get(option.tag)} />
								</span>
							) : (
								<>
									<span className="truncate">
										<TagChip tag={option.tag} color={colors.get(option.tag)} />
										{present.has(option.tag) && (
											<span className="ml-[7px] text-ink-faint">не у всех</span>
										)}
									</span>
									<span className="tabular text-ink-faint">{option.count}</span>
								</>
							)}
						</button>
					</li>
				))}
				{options.length === 0 && (
					<li className="px-3 py-2 text-xs font-light text-ink-muted">
						{allTags.length === 0 && typed === ""
							? "тегов пока нет — набери первый"
							: typed === ""
								? "все теги уже стоят"
								: "уже стоит"}
					</li>
				)}
			</ul>

			<div className="flex items-center justify-between gap-3 border-t border-stroke-soft px-4 py-2">
				<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-light text-ink-muted">
					<span>
						<span className="kbd">Enter</span> добавить
					</span>
					<span>
						<span className="kbd">,</span> как набрано
					</span>
					<span>
						<span className="kbd">⌫</span> снять последний
					</span>
					<span>щелчок по тегу — цвет</span>
				</div>
				<button type="button" className="btn" onClick={() => actions.setOverlay(null)}>
					Готово <span className="kbd">Esc</span>
				</button>
			</div>
		</div>
	);
}

const TAG_COLOR_LABELS: Record<TagColor, string> = {
	red: "красный",
	orange: "оранжевый",
	amber: "янтарный",
	green: "зелёный",
	teal: "бирюзовый",
	blue: "синий",
	violet: "фиолетовый",
	pink: "розовый",
	gray: "серый"
};

/**
 * Палитра одного тега — полосой под строкой плашек, а не всплывашкой поверх: оверлей
 * и так всплывающий, второй слой над ним перекрыл бы подсказки, ради которых диалог открыт.
 */
function TagPalette({
	tag,
	color,
	onPick
}: {
	tag: string;
	color: TagColor | undefined;
	onPick: (color: TagColor | null) => void;
}) {
	return (
		<div className="flex flex-wrap items-center gap-x-[9px] gap-y-2 border-b border-stroke-soft px-4 py-[9px]">
			<span className="mr-1 text-xs font-light text-ink-muted">
				цвет <TagChip tag={tag} color={color} />
			</span>
			{TAG_COLORS.map((c) => (
				<button
					key={c}
					type="button"
					className="tag-swatch"
					data-color={c}
					aria-pressed={color === c}
					aria-label={TAG_COLOR_LABELS[c]}
					title={TAG_COLOR_LABELS[c]}
					onMouseDown={(e) => e.preventDefault()}
					onClick={() => onPick(c)}
				/>
			))}
			<button
				type="button"
				className="tag-swatch tag-swatch-none"
				aria-pressed={color === undefined}
				aria-label="без цвета"
				title="без цвета"
				onMouseDown={(e) => e.preventDefault()}
				onClick={() => onPick(null)}
			/>
		</div>
	);
}

/** Поиск по локальной реплике — перебором, мгновенно и без сети. */
function SearchBox({ onPick }: { onPick: (node: Node) => void }) {
	const [query, setQuery] = useState("");
	const results = useSearch(query);
	const [index, setIndex] = useState(0);
	const inputRef = useRef<HTMLInputElement>(null);
	const listRef = useScrollToActive(index);

	useEffect(() => {
		inputRef.current?.focus();
	}, []);
	useEffect(() => setIndex(0), [query]);

	return (
		<div>
			<input
				ref={inputRef}
				value={query}
				onChange={(e) => setQuery(e.target.value)}
				placeholder="заголовок или текст заметки"
				className="w-full border-b border-stroke-soft bg-transparent px-4 py-2 outline-none placeholder:text-ink-faint"
				onKeyDown={(e) => {
					if (e.key === "ArrowDown") {
						e.preventDefault();
						setIndex((i) => Math.min(results.length - 1, i + 1));
					} else if (e.key === "ArrowUp") {
						e.preventDefault();
						setIndex((i) => Math.max(0, i - 1));
					} else if (e.key === "Enter") {
						e.preventDefault();
						const node = results[index];
						if (node) {
							onPick(node);
						}
					}
				}}
			/>
			<ul ref={listRef} className="max-h-72 overflow-y-auto p-[5px]">
				{results.map((node, i) => (
					<li key={node.id}>
						<button
							type="button"
							className={`menu-item block w-full truncate rounded-sm px-3 py-[5px] text-left text-xs font-light ${
								i === index ? "bg-active text-ink-strong" : ""
							}`}
							/* Именно `mousemove`, а не `mouseenter`: при автоскролле список едет
							   под неподвижным курсором и `mouseenter` перебивал бы выбор с клавиатуры. */
							onMouseMove={() => setIndex(i)}
							onClick={() => onPick(node)}
						>
							{node.title || "без названия"}
						</button>
					</li>
				))}
				{query !== "" && results.length === 0 && (
					<li className="px-3 py-2 text-xs font-light text-ink-muted">ничего не нашлось</li>
				)}
			</ul>
		</div>
	);
}

/**
 * Палитра команд по `aa`. Ищет КОМАНДЫ, а не ноды — выдачи не смешиваются
 * (ui-spec Область 10).
 */
function CommandPalette({ engine }: { engine: ReturnType<typeof useOutlineEngine> }) {
	const { actions } = engine;
	const dispatch = useDispatch();
	const store = useStore();

	const commands = useMemo(
		() => [
			{ label: "Редактировать строку", hint: "Enter", run: () => actions.edit(null) },
			{ label: "Новая строка ниже", hint: "⌘Enter", run: () => actions.createSibling("below") },
			{ label: "Новая дочерняя строка", hint: "⇧Enter", run: () => actions.createSibling("child") },
			{ label: "Отступ", hint: "Tab", run: actions.indent },
			{ label: "Выступ", hint: "⇧Tab", run: actions.outdent },
			{ label: "Выполнено / снять", hint: "Space", run: actions.toggleDone },
			{ label: "Не буду делать", hint: "⇧Space", run: () => actions.setStatus("cancelled") },
			{ label: "Статус…", hint: "ss", run: () => actions.setOverlay({ kind: "status" }) },
			{ label: "Роль…", hint: "rt/rp/rn/rf", run: () => actions.setOverlay({ kind: "role" }) },
			{ label: "Сделать фокусом", hint: "rf", run: () => actions.setRole("focus") },
			{ label: "Срок…", hint: "dd", run: () => actions.setOverlay({ kind: "date" }) },
			{ label: "Повторение…", hint: "dr", run: () => actions.setOverlay({ kind: "recurrence" }) },
			{ label: "Теги…", hint: "tt", run: () => actions.setOverlay({ kind: "tags" }) },
			{ label: "Заметка", hint: "nn", run: actions.toggleBody },
			{ label: "Запинить / открепить", hint: "pp", run: actions.togglePin },
			{
				label: "Вплести в…",
				hint: "pl",
				run: () => actions.setOverlay({ kind: "picker", purpose: "placement" })
			},
			{ label: "Переместить в…", hint: "mm", run: () => actions.setOverlay({ kind: "picker", purpose: "move" }) },
			{ label: "Вставить ссылкой", hint: "⇧⌘V", run: actions.pasteLink },
			{ label: "Архивировать / вернуть", hint: "ar", run: actions.archiveOrRestore },
			{ label: "Удалить", hint: "Del", run: actions.removeSelection },
			{ label: "Отменить действие", hint: "⌘Z", run: actions.undo },
			{
				label: "Вставить текст как дерево…",
				hint: "⌘V",
				run: () => {
					void navigator.clipboard?.readText().then((text) => text && actions.pasteText(text));
				}
			},
			{ label: "Сменить тему", hint: "", run: toggleTheme },
			{
				label: "Сменить таймзону…",
				hint: "",
				run: () => {
					const tz = prompt("Таймзона (IANA)", store.getSnapshot().tz);
					if (tz) {
						dispatch({ name: "setTimezone", input: { tz } });
					}
				}
			},
			{ label: "Сегодня", hint: "gt", run: () => actions.go("/today") },
			{ label: "Входящие", hint: "gi", run: () => actions.go("/inbox") },
			{ label: "Проекты", hint: "gp", run: () => actions.go("/projects") },
			{ label: "Календарь", hint: "gc", run: () => actions.go("/calendar") },
			{ label: "Повторяющиеся", hint: "gr", run: () => actions.go("/recurring") },
			{ label: "Просрочено", hint: "gd", run: () => actions.go("/overdue") },
			{ label: "Пины", hint: "gf", run: () => actions.go("/pinned") },
			{ label: "Фокус", hint: "go", run: () => actions.go("/focus") },
			{ label: "Архив", hint: "ga", run: () => actions.go("/archive") },
			{ label: "Конфликты", hint: "", run: () => actions.go("/conflicts") }
		],
		[actions, dispatch, store]
	);

	const [query, setQuery] = useState("");
	const [index, setIndex] = useState(0);
	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		return q === "" ? commands : commands.filter((c) => c.label.toLowerCase().includes(q) || c.hint.includes(q));
	}, [commands, query]);

	const inputRef = useRef<HTMLInputElement>(null);
	const listRef = useScrollToActive(index);
	const phone = usePhone();
	useEffect(() => {
		// На телефоне палитру открывают, чтобы ВЫБРАТЬ, а не искать: список короткий и
		// весь на виду. Автофокус там поднял бы клавиатуру и закрыл ею половину команд.
		if (!phone) {
			inputRef.current?.focus();
		}
	}, [phone]);

	return (
		<div>
			<input
				ref={inputRef}
				value={query}
				onChange={(e) => {
					setQuery(e.target.value);
					setIndex(0);
				}}
				placeholder="что сделать"
				className="w-full border-b border-stroke-soft bg-transparent px-4 py-2 outline-none placeholder:text-ink-faint"
				onKeyDown={(e) => {
					if (e.key === "ArrowDown") {
						e.preventDefault();
						setIndex((i) => Math.min(filtered.length - 1, i + 1));
					} else if (e.key === "ArrowUp") {
						e.preventDefault();
						setIndex((i) => Math.max(0, i - 1));
					} else if (e.key === "Enter") {
						e.preventDefault();
						const command = filtered[index];
						if (command) {
							actions.setOverlay(null);
							command.run();
						}
					}
				}}
			/>
			<ul ref={listRef} className="max-h-80 overflow-y-auto p-[5px]">
				{filtered.map((command, i) => (
					<li key={command.label}>
						<button
							type="button"
							className={`menu-item flex w-full items-baseline justify-between gap-[9px] rounded-sm px-3 py-[5px] text-left text-xs font-light ${
								i === index ? "bg-active text-ink-strong" : ""
							}`}
							/* См. `SearchBox`: `mousemove`, чтобы автоскролл не перехватывал выбор. */
							onMouseMove={() => setIndex(i)}
							onClick={() => {
								actions.setOverlay(null);
								command.run();
							}}
						>
							<span>{command.label}</span>
							{command.hint ? <span className="kbd">{command.hint}</span> : null}
						</button>
					</li>
				))}
			</ul>
		</div>
	);
}

function shift(date: string, days: number): string {
	const [y, m, d] = date.split("-").map(Number) as [number, number, number];
	return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** Тема — атрибут на `<html>`, а не media query: обе лежат в одном бандле, переключение мгновенное. */
export function toggleTheme(): void {
	const root = document.documentElement;
	const next = root.dataset.theme === "light" ? "dark" : "light";
	root.dataset.theme = next;
	syncStatusBarColor();
	try {
		localStorage.setItem("mapcraft:theme", next);
	} catch {
		// приватный режим — тема просто не переживёт перезагрузку
	}
}
